// Quiz service for saving quiz results to backend
import { supabase } from './supabase';
import { getApiUrl } from './apiConfig';

/**
 * Save quiz result to backend (GoScript MongoDB)
 * @param {Object} quizResult - Quiz result data
 * @returns {Promise<Object>} Response from server
 */
export const saveQuizResult = async (quizResult) => {
  try {
    // Get current user's JWT token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('❌ No active session, cannot save quiz result');
      throw new Error('User not authenticated');
    }

    const token = session.access_token;
    const userId = session.user.id;

    console.log('💾 Saving quiz result for user:', userId);

    // Prepare quiz data
    const quizData = {
      quiz_id: quizResult.quiz_id || `quiz_${Date.now()}`,
      subjectId: quizResult.subjectId || 'general',
      subject_name: quizResult.subject_name || 'General',
      topicId: quizResult.topicId || 'reading',
      topic_name: quizResult.topic_name || 'Reading Session',
      title: quizResult.title || 'Quiz',
      question_text: quizResult.question_text || '',
      options: quizResult.options || [],
      student_answer: quizResult.student_answer || '',
      correct_answer: quizResult.correct_answer || '',
      isCorrect: quizResult.isCorrect || false,
      scoreAwarded: quizResult.scoreAwarded || 0
    };

    console.log('📤 Sending quiz data:', quizData);

    // Send to GoScript backend
    const response = await fetch(getApiUrl('quizzes/create'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(quizData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Failed to save quiz result:', errorData);
      throw new Error(errorData.error || 'Failed to save quiz result');
    }

    const result = await response.json();
    console.log('✅ Quiz result saved successfully:', result);
    
    return {
      success: true,
      data: result
    };

  } catch (error) {
    console.error('❌ Error saving quiz result:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get user's quiz history from backend
 * @returns {Promise<Array>} Array of quiz results
 */
export const getUserQuizzes = async () => {
  try {
    // Get current user's JWT token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('❌ No active session');
      throw new Error('User not authenticated');
    }

    const token = session.access_token;

    const response = await fetch(getApiUrl('quizzes'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch quiz history');
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('❌ Error fetching quiz history:', error);
    return {
      quizzes: [],
      total_quizzes: 0
    };
  }
};

/**
 * Get user's quiz statistics
 * @returns {Promise<Object>} Quiz statistics
 */
export const getQuizStats = async () => {
  try {
    // Get current user's JWT token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('❌ No active session');
      throw new Error('User not authenticated');
    }

    const token = session.access_token;

    const response = await fetch(getApiUrl('quizzes/stats'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch quiz stats');
    }

    const stats = await response.json();
    console.log('📊 Quiz stats:', stats);
    return stats;

  } catch (error) {
    console.error('❌ Error fetching quiz stats:', error);
    return {
      total_quizzes: 0,
      correct: 0,
      incorrect: 0,
      accuracy: 0,
      total_score: 0
    };
  }
};

/**
 * Get user's subject-based quiz scores for dashboard ranking
 * @returns {Promise<Object>} Subject ranking data
 */
export const getSubjectQuizScores = async () => {
  try {
    // Get current user's JWT token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('❌ No active session');
      throw new Error('User not authenticated');
    }

    const token = session.access_token;

    console.log('🔑 Using token:', token.substring(0, 50) + '...');
    
    const response = await fetch(getApiUrl('quizzes/subject-scores'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      throw new Error(`Failed to fetch subject quiz scores: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 Subject quiz scores received:', data);
    return data;

  } catch (error) {
    console.error('❌ Error fetching subject quiz scores:', error);
    console.error('❌ Error details:', error.message);
    return {
      user_id: '',
      subject_ranking: [],
      total_subjects: 0
    };
  }
};

export const getTopicMasteryData = async () => {
  try {
    // Get current user's JWT token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('❌ No active session');
      throw new Error('User not authenticated');
    }

    const token = session.access_token;

    console.log('🔑 Using token for topic mastery:', token.substring(0, 50) + '...');
    
    const response = await fetch(getApiUrl('quizzes/topic-mastery'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('📡 Topic mastery response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      throw new Error(`Failed to fetch topic mastery data: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('📊 Topic mastery data received:', data);
    return data;

  } catch (error) {
    console.error('❌ Error fetching topic mastery data:', error);
    console.error('❌ Error details:', error.message);
    return {
      user_id: '',
      topic_mastery: [],
      total_subjects: 0
    };
  }
};
